import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const base_url = process.env.REACT_APP_API_URL;

export default function UsersList() {
  const navigate = useNavigate();
  const token = localStorage.getItem("admin_token");

  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [loading, setLoading] = useState(false);

  /* ---------------- FETCH USERS ---------------- */

  const fetchUsers = async (p = page, s = search, st = status) => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${base_url}/api/admin/users?page=${p}&limit=${limit}&search=${s}&status=${st}`,
        { headers: { Authorization: token } },
      );

      if (res.data.success) {
        setData(res.data.data || []);
        setPage(res.data.pagination.page);
        setTotalPages(res.data.pagination.totalPages);
      }
    } catch (err) {
      toast.error("Failed to load users");
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  /* ---------------- SEARCH ---------------- */

  const handleSearch = (e) => {
    const value = e.target.value;

    setSearch(value);

    fetchUsers(1, value, status);
  };

  /* ---------------- STATUS FILTER ---------------- */

  const handleStatus = (e) => {
    const value = e.target.value;

    setStatus(value);

    fetchUsers(1, search, value);
  };

  /* ---------------- UPDATE BALANCE ---------------- */

  const updateBalance = async (user) => {
    const { value: formValues } = await Swal.fire({
      title: "Update Balance",

      html: `
      <select id="action" class="swal2-input">
        <option value="deposit">Deposit</option>
        <option value="withdraw">Withdraw</option>
      </select>

      <input id="amount" type="number" class="swal2-input" placeholder="Enter amount">
      `,

      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: "Update",
      cancelButtonText: "Close",
      focusConfirm: false,

      preConfirm: () => {
        const action = document.getElementById("action").value;
        const amount = document.getElementById("amount").value;

        if (!amount) {
          Swal.showValidationMessage("Amount required");
          return false;
        }

        return { action, amount };
      },
    });

    if (!formValues) return;

    await axios.post(
      `${base_url}/api/admin/update-user-money`,
      {
        phone: user.phone,
        action: formValues.action,
        amount: formValues.amount,
      },
      { headers: { Authorization: token } },
    );

    toast.success("Balance updated");

    fetchUsers();
  };

  /* ---------------- UPDATE REMAINING BET ---------------- */

  const updateBet = async (user) => {
    const { value: amount } = await Swal.fire({
      title: "Update Remaining Bet",

      input: "number",

      inputValue: user.remaining_money,

      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: "Update",
      cancelButtonText: "Close",
    });

    if (!amount) return;

    await axios.post(
      `${base_url}/api/admin/update-remaining-bet`,
      { phone: user.phone, remaining_money: amount },
      { headers: { Authorization: token } },
    );

    toast.success("Remaining bet updated");

    fetchUsers();
  };

  /* ---------------- BLOCK / OPEN USER ---------------- */

  const updateStatus = async (user, newStatus) => {
    await axios.post(
      `${base_url}/api/admin/update-user-status`,
      { phone: user.phone, status: newStatus },
      { headers: { Authorization: token } },
    );

    toast.success("User status updated");

    fetchUsers();
  };

  return (
    <div className="px-4">
      <h4 className="mb-3">Users List</h4>

      {/* ---------------- FILTERS ---------------- */}

      <div className="row mb-3">
        <div className="col-md-4">
          <input
            className="form-control"
            placeholder="Search phone or name"
            value={search}
            onChange={handleSearch}
          />
        </div>

        <div className="col-md-3">
          <select
            className="form-control"
            value={status}
            onChange={handleStatus}
          >
            <option value="">All Users</option>
            <option value="1">Active</option>
            <option value="0">Blocked</option>
          </select>
        </div>
      </div>

      {/* ---------------- TABLE ---------------- */}

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Password</th>
                <th>Own Code</th>
                <th>Invite By</th>
                <th>Balance</th>
                <th>Remaining Bet</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    Loading...
                  </td>
                </tr>
              ) : data.length > 0 ? (
                data.map((item, index) => (
                  <tr key={item.phone}>
                    <td>{(page - 1) * limit + index + 1}</td>

                    <td>{item.name_user}</td>
                    <td>{item.phone}</td>
                    <td>{item.plain_password}</td>
                    <td>{item.code}</td>
                    <td>{item.invite ?? "N/A"}</td>

                    <td className="fw-bold text-success">
                      ₹{parseFloat(item.money).toFixed(2)}
                    </td>

                    <td>{item.remaining_money.toFixed(2)}</td>

                    <td>
                      {item.status == 1 ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-danger">Blocked</span>
                      )}
                    </td>

                    <td>
                      <div className="d-flex align-items-center justify-content-center gap-2">

                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() =>
                            navigate(`/admin/user-profile/${item.phone}`)
                          }
                        >
                          Profile
                        </button>
                        
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={() => updateBalance(item)}
                        >
                          Balance
                        </button>

                        <button
                          className="btn btn-info btn-sm"
                          onClick={() => updateBet(item)}
                        >
                          Bet
                        </button>

                        {item.status == 1 ? (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => updateStatus(item, 0)}
                          >
                            Block
                          </button>
                        ) : (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => updateStatus(item, 1)}
                          >
                            Open
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------------- PAGINATION ---------------- */}

      <div className="d-flex justify-content-center gap-3 mt-3">
        <button
          className="btn btn-secondary"
          disabled={page === 1}
          onClick={() => fetchUsers(page - 1)}
        >
          Prev
        </button>

        <span>
          Page {page} / {totalPages}
        </span>

        <button
          className="btn btn-secondary"
          disabled={page === totalPages}
          onClick={() => fetchUsers(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
